import { isAdminRoute } from "./isAdminRoute";

const TAB_ID_KEY_PREFIX = "app_session_tab_id";
const SESSION_ACTIVE_KEY_PREFIX = "app_session_active";
const CHANNEL_NAME = "app_session_channel";
// Legacy keys from before userId scoping — cleared on init/reload.
const LEGACY_SESSION_ACTIVE_KEY = "app_session_active";
const LEGACY_TAB_ID_PREFIX = "app_session_tab_id_";

let tabId: string;
let currentUserId: string | null = null;
let broadcastChannel: BroadcastChannel | null = null;
let isActiveTab = false;
let cleanupFunction: (() => void) | null = null;
let onSessionClaimedCallback: (() => void) | null = null;
let onMultipleTabsDetectedCallback: (() => void) | null = null;

const sessionActiveKey = (userId: string) =>
  `${SESSION_ACTIVE_KEY_PREFIX}_${userId}`;

const tabHeartbeatKey = (userId: string, id: string) =>
  `${TAB_ID_KEY_PREFIX}_${userId}_${id}`;

const clearLegacyMultiTabKeys = () => {
  try {
    localStorage.removeItem(LEGACY_SESSION_ACTIVE_KEY);
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      // Old format: app_session_tab_id_<tabId> (no userId segment after prefix)
      // New format: app_session_tab_id_<userId>_<tabId>
      if (!key.startsWith(LEGACY_TAB_ID_PREFIX)) return;
      const rest = key.slice(LEGACY_TAB_ID_PREFIX.length);
      if (!rest.includes("_") || rest.split("_").length === 1) {
        localStorage.removeItem(key);
      }
    });
  } catch {
    // Ignore errors
  }
};

const clearMultiTabSessionStateForUser = (userId: string) => {
  try {
    localStorage.removeItem(sessionActiveKey(userId));
    const prefix = `${TAB_ID_KEY_PREFIX}_${userId}_`;
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    });
  } catch {
    // Ignore errors
  }
};

const isPageReload = (): boolean => {
  try {
    const navEntry = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    return navEntry?.type === "reload";
  } catch {
    return false;
  }
};

const pruneStaleHeartbeats = (userId: string) => {
  try {
    const prefix = `${TAB_ID_KEY_PREFIX}_${userId}_`;
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (!key.startsWith(prefix)) return;
      const heartbeatTime = parseInt(localStorage.getItem(key) || "0", 10);
      if (Date.now() - heartbeatTime > 10000) {
        localStorage.removeItem(key);
      }
    });
  } catch {
    // Ignore errors
  }
};

export const initMultiTabDetection = (
  userId: string,
  onMultipleTabsDetected: () => void,
  onSessionClaimed?: () => void
): (() => void) => {
  if (isAdminRoute()) {
    return () => {};
  }

  if (!userId) {
    return () => {};
  }

  // Re-init for a new user: tear down previous listeners first.
  if (cleanupFunction) {
    cleanupFunction();
    cleanupFunction = null;
  }

  clearLegacyMultiTabKeys();

  // beforeunload is unreliable on reload — stale heartbeats look like another tab
  if (isPageReload()) {
    clearMultiTabSessionStateForUser(userId);
  }

  currentUserId = userId;
  onSessionClaimedCallback = onSessionClaimed || null;
  onMultipleTabsDetectedCallback = onMultipleTabsDetected;
  tabId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  isActiveTab = false;

  pruneStaleHeartbeats(userId);

  const activeKey = sessionActiveKey(userId);
  const existingTabId = localStorage.getItem(activeKey);
  let detectedMultipleTabs = false;

  if (existingTabId && existingTabId !== tabId) {
    const heartbeat = localStorage.getItem(tabHeartbeatKey(userId, existingTabId));
    if (heartbeat) {
      const heartbeatTime = parseInt(heartbeat, 10);
      const now = Date.now();
      if (now - heartbeatTime < 10000) {
        console.warn(
          "Another tab is already active for this account. Disconnecting immediately..."
        );
        detectedMultipleTabs = true;
        onMultipleTabsDetected();
      } else {
        localStorage.removeItem(tabHeartbeatKey(userId, existingTabId));
        if (existingTabId === localStorage.getItem(activeKey)) {
          localStorage.removeItem(activeKey);
        }
      }
    }
  }

  if (typeof BroadcastChannel !== "undefined") {
    broadcastChannel = new BroadcastChannel(CHANNEL_NAME);

    broadcastChannel.onmessage = (event) => {
      if (event.data?.userId && event.data.userId !== currentUserId) {
        return;
      }

      if (event.data.type === "session_claimed") {
        if (event.data.tabId !== tabId) {
          if (isActiveTab) {
            releaseSession();
            return;
          }
          console.warn("Another tab claimed the session. Disconnecting...");
          onMultipleTabsDetectedCallback?.();
        }
      } else if (event.data.type === "tab_disconnect") {
        if (event.data.tabId !== tabId && !isActiveTab) {
          const currentActiveTab = localStorage.getItem(
            sessionActiveKey(currentUserId!)
          );
          if (!currentActiveTab || currentActiveTab === event.data.tabId) {
            checkAndClaimSession();
          }
        }
      }
    };
  }

  const handleStorageChange = (e: StorageEvent) => {
    if (!currentUserId) return;

    const activeKeyForUser = sessionActiveKey(currentUserId);

    if (e.key === activeKeyForUser) {
      if (e.newValue && e.newValue !== tabId) {
        const newTabId = e.newValue;

        if (isActiveTab) {
          releaseSession();
          return;
        }

        setTimeout(() => {
          const currentActiveTab = localStorage.getItem(activeKeyForUser);
          if (currentActiveTab === newTabId && newTabId !== tabId) {
            const heartbeat = localStorage.getItem(
              tabHeartbeatKey(currentUserId!, newTabId)
            );
            if (heartbeat) {
              const heartbeatTime = parseInt(heartbeat, 10);
              const now = Date.now();
              if (now - heartbeatTime < 10000) {
                console.warn(
                  "Another tab is already active for this account. Disconnecting..."
                );
                onMultipleTabsDetectedCallback?.();
              }
            }
          }
        }, 100);
      } else if (!e.newValue && isActiveTab) {
        maintainSession();
      }
    } else if (
      e.key?.startsWith(`${TAB_ID_KEY_PREFIX}_${currentUserId}_`) &&
      !isActiveTab
    ) {
      const heartbeatTabId = e.key.replace(
        `${TAB_ID_KEY_PREFIX}_${currentUserId}_`,
        ""
      );
      const currentActiveTab = localStorage.getItem(activeKeyForUser);
      if (heartbeatTabId === currentActiveTab && heartbeatTabId !== tabId) {
        console.warn("Active tab heartbeat detected. Disconnecting...");
        onMultipleTabsDetectedCallback?.();
      }
    }
  };

  window.addEventListener("storage", handleStorageChange);

  if (!detectedMultipleTabs) {
    claimSession();
  }

  const heartbeatInterval = setInterval(() => {
    if (isActiveTab) {
      maintainSession();
    } else {
      checkAndClaimSession();
    }
  }, 2000);

  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible" && !isActiveTab) {
      checkAndClaimSession();
    } else if (document.visibilityState === "visible" && isActiveTab) {
      maintainSession();
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);

  const handleBeforeUnload = () => {
    releaseSession();
  };

  window.addEventListener("beforeunload", handleBeforeUnload);

  cleanupFunction = () => {
    if (broadcastChannel) {
      broadcastChannel.close();
      broadcastChannel = null;
    }
    window.removeEventListener("storage", handleStorageChange);
    window.removeEventListener("beforeunload", handleBeforeUnload);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    clearInterval(heartbeatInterval);
    releaseSession();
    currentUserId = null;
    onMultipleTabsDetectedCallback = null;
    onSessionClaimedCallback = null;
    cleanupFunction = null;
  };

  return cleanupFunction;
};

const claimSession = () => {
  if (!currentUserId) return;

  const wasInactive = !isActiveTab;
  isActiveTab = true;
  localStorage.setItem(sessionActiveKey(currentUserId), tabId);
  maintainSession();

  if (broadcastChannel) {
    broadcastChannel.postMessage({
      type: "session_claimed",
      tabId: tabId,
      userId: currentUserId,
    });
  }

  if (wasInactive && onSessionClaimedCallback) {
    onSessionClaimedCallback();
  }
};

const checkAndClaimSession = () => {
  if (!currentUserId) return;

  pruneStaleHeartbeats(currentUserId);

  const existingTabId = localStorage.getItem(sessionActiveKey(currentUserId));

  if (!existingTabId || existingTabId === tabId) {
    claimSession();
  } else {
    const heartbeat = localStorage.getItem(
      tabHeartbeatKey(currentUserId, existingTabId)
    );
    if (!heartbeat) {
      claimSession();
    } else {
      const heartbeatTime = parseInt(heartbeat, 10);
      const now = Date.now();
      if (now - heartbeatTime >= 10000) {
        claimSession();
      }
    }
  }
};

const maintainSession = () => {
  if (!currentUserId) return;

  localStorage.setItem(sessionActiveKey(currentUserId), tabId);
  localStorage.setItem(
    tabHeartbeatKey(currentUserId, tabId),
    Date.now().toString()
  );

  pruneStaleHeartbeats(currentUserId);
};

const releaseSession = () => {
  if (!currentUserId) {
    isActiveTab = false;
    return;
  }

  if (isActiveTab) {
    isActiveTab = false;
    const activeKey = sessionActiveKey(currentUserId);
    const currentActiveTab = localStorage.getItem(activeKey);
    if (currentActiveTab === tabId) {
      localStorage.removeItem(activeKey);
    }
    localStorage.removeItem(tabHeartbeatKey(currentUserId, tabId));

    if (broadcastChannel) {
      broadcastChannel.postMessage({
        type: "tab_disconnect",
        tabId: tabId,
        userId: currentUserId,
      });
    }
  }
};

export const isActiveSessionTab = (): boolean => {
  return isActiveTab;
};

export const forceReleaseSession = () => {
  releaseSession();
};

export const forceClaimSession = () => {
  claimSession();
};

// Clear multi-tab locks for the given user (or the current multi-tab user).
export const clearMultiTabSessionState = (userId?: string) => {
  const targetUserId = userId || currentUserId;
  if (targetUserId) {
    clearMultiTabSessionStateForUser(targetUserId);
  }
  clearLegacyMultiTabKeys();
};

export const getMultiTabUserId = (): string | null => currentUserId;
